import React from "react";
import { Link } from "react-router-dom";
import { Users, MapPin } from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import api from "../../lib/apiClient";
import { profilePrefetchProps } from "../../lib/prefetch";

/**
 * FeedNearbyProducersPanel — list of registered producers whose home
 * city is near the currently-playing video's filming location.
 *
 * Iter-57+++++ (May 28, 2026): introduced as the new bottom slot of the
 * right column (above-the-fold: Nearby Map · below-the-fold: this rail).
 * Backend endpoint: GET /api/videos/{id}/nearby-producers — returns
 * producers sorted by distance with hydrated video + follower counts.
 *
 * No outer rounded chrome — parent's `divide-y` container owns the
 * card frame so this block reads as a connected sub-section of the
 * unified right-column card.
 */
const FeedNearbyProducersPanel = ({ activeVideo, handleClosePlayer }) => {
  const videoId = activeVideo?.id;
  const [producers, setProducers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (!videoId) { setProducers([]); return; }
    const fetchNearby = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/videos/${videoId}/nearby-producers?radius_km=500&limit=12`);
        if (!cancelled) setProducers(r.data?.producers || []);
      } catch (_err) {
        if (!cancelled) setProducers([]);
      }
      if (!cancelled) setLoading(false);
    };
    fetchNearby();
    return () => { cancelled = true; };
  }, [videoId]);

  return (
    <div className="flex p-2.5 flex-1 flex-col min-h-0" data-testid="feed-nearby-producers">
      <p className="text-gray-400 text-[10px] uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
        <Users className="h-3 w-3 text-emerald-400" /> Nearby Producers
      </p>
      <ScrollArea className="flex-1 -mx-1 px-1 max-h-[180px] lg:max-h-none">
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : producers.length === 0 ? (
          <p className="text-gray-600 text-[11px] italic py-3 text-center">No registered producers nearby yet.</p>
        ) : (
          <div className="space-y-1.5">
            {producers.map((p) => (
              <Link
                key={p.username}
                to={`/${p.username}`}
                onClick={handleClosePlayer}
                {...profilePrefetchProps(p.username)}
                className="block p-2 rounded bg-gray-800/40 hover:bg-gray-700/50 transition border border-gray-800/50 hover:border-emerald-500/30"
                data-testid={`nearby-producer-${p.username}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-emerald-400 font-medium text-[12px] truncate">@{p.username}</span>
                  <span className="text-gray-500 tabular-nums text-[10px] flex-shrink-0">
                    {p.distance_km < 10 ? `${p.distance_km.toFixed(1)} km` : `${Math.round(p.distance_km)} km`}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                  {p.home_city && (
                    <span className="flex items-center gap-0.5 truncate">
                      <MapPin className="h-2.5 w-2.5 text-gray-600 flex-shrink-0" />
                      <span className="truncate">{p.home_city}{p.home_city_country ? `, ${p.home_city_country}` : ""}</span>
                    </span>
                  )}
                  {p.video_count > 0 && (
                    <span className="text-gray-500 tabular-nums flex-shrink-0">· {p.video_count}🎬</span>
                  )}
                  {p.follower_count > 0 && (
                    <span className="text-gray-500 tabular-nums flex-shrink-0">· {p.follower_count}👥</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default FeedNearbyProducersPanel;
